import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { revokePreviewUrls } from '../utils/photoPreview';
import { ClickablePhoto, usePhotoLightbox } from './PhotoLightbox';

const KITCHEN_SUMMARY_SLOTS = [
  { step: 1, label: 'Entire kitchen area facing inward' },
  { step: 2, label: 'Range area' },
  { step: 3, label: 'Kitchen island (optional)' },
  { step: 4, label: 'Windows or patio doors' },
];

const OPTIONAL_STEPS = new Set([3]);

const PhotoUploadModal2 = ({ PhotoUpload, PhotoUploadToggle, ThankyouOpen, loadBtn, BathroomDataResponse, BathroomDataImg }) => {
  const [step, setStep] = useState(1);
  const [images, setImages] = useState({});
  const [imagesData, setImagesData] = useState({});
  const [errors, setErrors] = useState({});
  const totalSteps = 5;
  const [Multipleimages, setMultipleImages] = useState([]);
  const { openLightbox, closeLightbox } = usePhotoLightbox();

  useEffect(() => {
    if (!PhotoUpload) {
      const timer = window.setTimeout(() => closeLightbox(), 0);
      setImages((prev) => {
        revokePreviewUrls(Object.values(prev));
        return {};
      });
      setMultipleImages((prev) => {
        revokePreviewUrls(prev.map((img) => img.id));
        return [];
      });
      setStep(1);
      setErrors({});
      setImagesData({});
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [PhotoUpload, closeLightbox]);

  const assignStepImage = (stepNumber, file) => {
    const questionByStep = {
      1: 'kitchen1',
      2: 'kitchen2',
      3: 'kitchen3',
      4: 'kitchen4',
    };
    const question = questionByStep[stepNumber];
    if (!question) return;

    setImages((prev) => {
      if (prev[stepNumber]) revokePreviewUrls([prev[stepNumber]]);
      return { ...prev, [stepNumber]: URL.createObjectURL(file) };
    });
    setImagesData((prev) => ({ ...prev, [question]: file }));
    setErrors((prev) => ({ ...prev, [stepNumber]: '' }));
  };

  const handleDropForStep = (event, stepNumber) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      assignStepImage(stepNumber, file);
    }
  };

  const handleMultipleFileChange = (event) => {
    const files = Array.from(event.target.files);
    previewImages(files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    previewImages(files);
  };

  const previewImages = (files) => {
    const newImages = files.map((file) => ({
      id: URL.createObjectURL(file),
      file,
    }));
    setMultipleImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (id) => {
    revokePreviewUrls([id]);
    setMultipleImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleRemoveImage = (stepNumber) => {
    const questionByStep = {
      1: 'kitchen1',
      2: 'kitchen2',
      3: 'kitchen3',
      4: 'kitchen4',
    };

    setImages((prev) => {
      if (prev[stepNumber]) revokePreviewUrls([prev[stepNumber]]);
      const updatedImages = { ...prev };
      delete updatedImages[stepNumber];
      return updatedImages;
    });

    setImagesData((prev) => {
      const next = { ...prev };
      delete next[questionByStep[stepNumber]];
      return next;
    });

    setErrors((prev) => ({
      ...prev,
      [stepNumber]: OPTIONAL_STEPS.has(stepNumber)
        ? ''
        : 'Please upload an image before proceeding.',
    }));
  };

  const submitBtn = () => {
    ThankyouOpen(imagesData, BathroomDataResponse, BathroomDataImg, Multipleimages);
  };

  const handleFileChange = (event, stepNumber) => {
    const file = event.target.files[0];
    if (file) {
      assignStepImage(stepNumber, file);
      event.target.value = '';
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      if (!images[step] && !OPTIONAL_STEPS.has(step)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [step]: 'Please upload an image before proceeding.',
        }));
        return;
      }
      setStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((prevStep) => prevStep - 1);
  };

  return (
    <>
      <Modal show={PhotoUpload} onHide={PhotoUploadToggle} centered size="lg" className={`modal-getstarted rs-re-modal-soft-open${step === 5 ? ' rs-re-photo-summary' : ''}`}>
        <button type="button" className="rs-lc-close" onClick={PhotoUploadToggle} aria-label="Close">
          <Icon icon="tabler:x" width={22} height={22} />
        </button>
        <Modal.Body className="rs-lc-body pt-2">
          {step < 5 ? (
            <div className="rs-re-upload-header mb-50">
              <div className="section-heading">
                <h4 className="subheading">{step}/4</h4>
                <h2>Photo Upload</h2>
              </div>
              <div className="rs-re-upload-actions">
                {step > 1 && (
                  <Button variant="outline-primary" onClick={handleBack}>
                    Back
                  </Button>
                )}
                <Button onClick={handleNext}>
                  Next <Icon icon="mynaui:arrow-right" width={20} height={20} />
                </Button>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap mb-50">
              <div className="section-heading">
                <h2>Photos Summary</h2>
              </div>
              <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                {step > 1 && (
                  <Button variant="outline-primary" onClick={handleBack}>
                    Back
                  </Button>
                )}
                <Button onClick={submitBtn} disabled={loadBtn}>
                  {loadBtn ? (
                    <Spinner color="#00316D" size={1} speed={1} animating={true} style={{ marginTop: '10px' }} />
                  ) : (
                    <>
                      Submit <Icon icon="mynaui:arrow-right" width="24" height="24" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step < 5 &&
            [1, 2, 3, 4].map((stepNumber) =>
              step === stepNumber ? (
                <div key={step} className="personalInfosteps d-block rs-re-step-panel">
                  <Form.Label className="uploadphoto_label d-block">
                    {stepNumber === 1 && 'Take the first picture from the entryway of the kitchen facing inward, capturing the entire kitchen area'}
                    {stepNumber === 2 && 'Now take a photo of the range area.'}
                    {stepNumber === 3 && 'Take a photo of the kitchen island (optional).'}
                    {stepNumber === 4 && 'Are there any windows or patio doors in your kitchen? If yes, take a picture of them.'}
                  </Form.Label>
                  {!images[stepNumber] && (
                    <>
                      <div
                        className="additional-div rs-re-upload-zone mt-2"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDropForStep(e, stepNumber)}
                      >
                        <Form.Label htmlFor={`uploadphotos${stepNumber}`}>
                          <Icon icon="garden:upload-fill-16" width={24} height={24} />
                          <h6>Upload photos</h6>
                          <p className="mb-0">Drag and drop here, or click to browse</p>
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id={`uploadphotos${stepNumber}`}
                          className="d-none"
                          accept="image/png,image/jpeg,image/webp,image/heic"
                          onChange={(event) => handleFileChange(event, stepNumber)}
                        />
                      </div>
                      {errors[stepNumber] && <p className="text-danger mt-2">{errors[stepNumber]}</p>}
                    </>
                  )}

                  {images[stepNumber] && (
                    <div className="rs-re-preview-grid mt-3">
                      <div className="preview-container uploadRemove_box rs-re-step-preview">
                        <ClickablePhoto
                          src={images[stepNumber]}
                          alt={`Uploaded preview for step ${stepNumber}`}
                          onView={openLightbox}
                        />
                        <button
                          type="button"
                          className="close-preview"
                          onClick={() => handleRemoveImage(stepNumber)}
                          aria-label="Remove photo"
                        >
                          <Icon icon="tabler:x" width={18} height={18} />
                        </button>
                      </div>
                      <p className="rs-re-upload-confirm">Photo uploaded — click to view full size, or tap × to replace</p>
                    </div>
                  )}
                </div>
              ) : null
            )}

          {step === 5 && (
            <Row key={step} className="g-0 rs-re-step-panel">
              <Col xs={12}>
                <div className="rs-re-summary">
                  <p className="rs-re-summary__lede">
                    Review your four required photos. Add optional extras below if you’d like.
                  </p>
                  <div className="rs-re-summary__grid" role="list">
                    {KITCHEN_SUMMARY_SLOTS.map(({ step: slotStep, label }, index) => (
                      <article key={slotStep} className="rs-re-summary-card" role="listitem">
                        <div className="rs-re-summary-card__head">
                          <span className="rs-re-summary-card__index" aria-hidden="true">
                            {index + 1}
                          </span>
                          <h3 className="rs-re-summary-card__label">{label}</h3>
                        </div>
                        <div className="rs-re-summary-card__media">
                          {images[slotStep] ? (
                            <ClickablePhoto
                              src={images[slotStep]}
                              alt={label}
                              onView={openLightbox}
                              fill
                            />
                          ) : (
                            <div className="rs-re-summary-card__empty" role="status">
                              {OPTIONAL_STEPS.has(slotStep) ? 'Optional — not uploaded' : 'No photo uploaded'}
                            </div>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>

                  <section className="rs-re-summary-extras" aria-labelledby="re-summary-extras-kit-heading">
                    <h3 id="re-summary-extras-kit-heading" className="rs-re-summary-extras__title">
                      Additional photos{' '}
                      <span className="rs-re-summary-extras__optional">(optional)</span>
                    </h3>
                    <p className="rs-re-summary-extras__hint">
                      Drag and drop here, or click to upload more angles or details.
                    </p>
                    <div className="rs-re-summary-extras__grid">
                      <div
                        className="rs-re-summary-dropzone additional-div"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <Form.Label htmlFor="uploadphotosnew-kitchen" className="rs-re-summary-dropzone__label">
                          <Icon icon="garden:upload-fill-16" width={28} height={28} />
                          <span className="rs-re-summary-dropzone__title">Add photos</span>
                          <span className="rs-re-summary-dropzone__sub">Drop files or browse</span>
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id="uploadphotosnew-kitchen"
                          className="d-none"
                          multiple
                          onChange={handleMultipleFileChange}
                          accept="image/*"
                        />
                      </div>
                      {Multipleimages.map((img) => (
                        <div
                          key={img.id}
                          className="rs-re-summary-thumb preview-container uploadRemove_box"
                        >
                          <ClickablePhoto
                            src={img.id}
                            alt="Additional uploaded photo"
                            onView={openLightbox}
                            fill
                          />
                          <button
                            type="button"
                            className="remove-btn close-preview"
                            onClick={() => removeImage(img.id)}
                            aria-label="Remove photo"
                          >
                            <Icon icon="tabler:x" width={18} height={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PhotoUploadModal2;
